/**
 * AI call campaigns (`/ai-calls/campaigns`): every campaign, its state and its
 * queue at a glance.
 *
 * The queue counts are the server's `groupBy` over the targets, never a stored
 * counter. A campaign is created from here and edited on its own page; nothing
 * on this page rings anybody.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Megaphone, Plus, RefreshCw } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { listAiCampaigns, type AiCallCampaign } from "../../../../lib/aiCampaigns";
import { Btn, EmptyState, PageHead, StatusPill, fmtDate, fmtNum } from "../../../ui";
import { AiCallsTabs } from "../AiCallsTabs";
import { campaignStatusTone } from "./campaignTone";

export function CampaignsPage({ user }: { user: StaffUser }) {
  const t = useT();
  const navigate = useNavigate();
  const canManage = userCanPlatform(user, "manage:crm.calls");
  const [rows, setRows] = useState<AiCallCampaign[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      setRows((await listAiCampaigns()).rows);
    } catch {
      setRows(null);
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHead
        title={t("aiCalls.title")}
        sub={t("aiCalls.campaigns.intro")}
        actions={
          <>
            <Btn onClick={() => void load()}>
              <RefreshCw className="size-3.5" />
              {t("common.refresh")}
            </Btn>
            {canManage ? (
              <Btn tone="dark" onClick={() => navigate("/ai-calls/campaigns/new")}>
                <Plus className="size-3.5" />
                {t("aiCalls.campaigns.new")}
              </Btn>
            ) : null}
          </>
        }
      />

      <AiCallsTabs current="campaigns" />

      <div className="crm-card overflow-hidden p-0">
        {rows === null && !error ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : error ? (
          <EmptyState icon={Megaphone} title={t("aiCalls.campaigns.errorTitle")} body={t("aiCalls.campaigns.errorBody")} />
        ) : rows && rows.length === 0 ? (
          <EmptyState icon={Megaphone} title={t("aiCalls.campaigns.emptyTitle")} body={t("aiCalls.campaigns.emptyBody")} />
        ) : (
          <ul className="divide-y divide-border">
            {(rows ?? []).map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <Link to={`/ai-calls/campaigns/${row.id}`} className="font-medium hover:underline">
                    {row.name}
                  </Link>
                  <p className="crm-sub mt-0.5 text-xs">
                    {t(`aiCalls.campaigns.kind.${row.kind}`)} ·{" "}
                    {row.angle === "alert" ? `${t("aiCalls.campaigns.angle.alert")} · ` : ""}
                    {t(`aiCalls.campaigns.source.${row.source}`)} ·{" "}
                    {t("aiCalls.campaigns.created", { date: fmtDate(row.createdAt) })}
                  </p>
                </div>
                <StatusPill tone={campaignStatusTone(row.status)}>{t(`aiCalls.campaigns.status.${row.status}`)}</StatusPill>
                <p className="crm-sub w-full text-xs sm:w-auto">
                  {t("aiCalls.campaigns.queueLine", {
                    waiting: fmtNum(row.targets.pending + row.targets.retry),
                    done: fmtNum(row.targets.done),
                    skipped: fmtNum(row.targets.skipped),
                  })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
